/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
 */

import { useEffect, useMemo, useRef } from "preact/hooks";
import { useMerchantSWR } from "../swr.js";
import { remoteResource } from "../contracts.js";
import { getClientConfig } from "./common.js";
import {
  createExchangeStatusPoller,
  readExchangeStatus,
  type ExchangeStatus,
} from "../exchangeStatus.js";

/** Mounted only by the payment-services route; configuration is fetched separately. */
export function useExchangeStatus() {
  const config = getClientConfig();
  // SWR's mutate() can resolve to cached data after a failed revalidation. Track
  // the actual fetch outcome so that stale next_download values cannot drive retries.
  const outcome = useMemo(
    () => ({
      key: ["getExchangeStatus", config.rootUrl.href],
      latest: undefined as ExchangeStatus[] | undefined,
    }),
    [config.rootUrl.href],
  );
  const swr = useMerchantSWR(
    outcome.key,
    async () => {
      try {
        return (outcome.latest = await readExchangeStatus(config));
      } catch (error) {
        outcome.latest = undefined;
        throw error;
      }
    },
    {
      revalidateOnMount: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    },
  );
  const { mutate } = swr;
  const poller = useRef<ReturnType<typeof createExchangeStatusPoller>>();
  useEffect(() => {
    const active = createExchangeStatusPoller(async () => {
      await mutate();
      return outcome.latest;
    });
    poller.current = active;
    const refresh = () => {
      void active.refresh();
    };
    refresh();
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    return () => {
      active.dispose();
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
      if (poller.current === active) poller.current = undefined;
    };
  }, [mutate, outcome]);

  return {
    ...remoteResource(swr),
    isLoading: swr.data === undefined && !swr.error,
    refresh: () => poller.current?.refresh() ?? Promise.resolve(),
  };
}
